import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  CustomFormFieldFG,
  CustomModal,
  FormFieldTypeFG,
  FormGenerator,
  LoadingData,
  RadioFormFieldFG,
  SelectFormFieldFG,
} from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores/selectors';
import { PreferencesModel } from '@models/Preferences';
import { isEmpty, isNil, mergeWith } from 'lodash';
import { usePreferencesDataSource } from './preferencesDataSource';
import { uploadSiteNoteDateFormatOptions, defaultPreferencesValues } from './Preferences.utils';
import { showOptionsFn } from '../Maps/Togglers';
import { EMetricSystem } from '@models/EMetricSystem';
import { Typography } from '@material-ui/core';

interface PreferencesProps {
  accountId?: number;
}

export const Preferences: React.FC<PreferencesProps> = ({ accountId }) => {
  const isForAccount = !isNil(accountId);

  const formikRef = useRef<FormikProps<PreferencesModel>>(null);

  const { userRole } = useAppSelector(selectedAccStateSelector) ?? {};
  const { accountPreferences, userPreferences, savePreferences, getAccountPreferences, loading } =
    usePreferencesDataSource();

  const [showModal, setShowModal] = useState(false);

  const buttonAndModalTitle = isForAccount ? <Trans>Account Preferences</Trans> : <Trans>Preferences</Trans>;

  const preferencesInitialValues = useMemo(() => {
    const data = isForAccount ? accountPreferences : userPreferences;
    return mergeWith(data ?? null, defaultPreferencesValues, (apiVal, defaultVal) =>
      isNil(apiVal) ? defaultVal : apiVal
    );
  }, [isForAccount, accountPreferences, userPreferences]);

  const handleSavePreferences = useCallback(
    (formik: FormikProps<PreferencesModel>, accountId: number | null) => {
      formik.submitForm().then(() => {
        savePreferences(formik.values, accountId, () => setShowModal(false));
      });
    },
    [savePreferences]
  );

  useEffect(() => {
    if (!isForAccount || Boolean(accountPreferences?.id)) return;

    getAccountPreferences(accountId);
  }, [accountId, isForAccount, accountPreferences?.id, getAccountPreferences]);

  const DmaLvlWrapper = (component: JSX.Element, indent?: boolean) => {
    return (
      <div
        className={`flex flex-col px-2 pt-4 mb-2 mt-1 border-2 border-grey rounded-lg relative ${
          indent ? 'ml-4 sm:ml-8' : ''
        }`}
      >
        <Typography className="px-2 text-grey absolute -top-3 left-2 bg-white">
          <Trans>DMA level</Trans>
        </Typography>
        {component}
      </div>
    );
  };

  return userRole === null || userRole === 'No access' ? (
    <></>
  ) : (
    <>
      <Button
        className="p-2"
        variant={isForAccount ? 'contained' : 'outlined'}
        style={{}}
        onClick={() => setShowModal(true)}
      >
        {buttonAndModalTitle}
      </Button>
      <CustomModal
        show={showModal}
        setShow={setShowModal}
        headerTitle={buttonAndModalTitle}
        actionButtons={[
          <Button
            onClick={() => handleSavePreferences(formikRef.current, isForAccount ? accountId : null)}
            disabled={loading}
            loading={loading}
          >
            <Trans>Save</Trans>
          </Button>,
        ]}
        maxWidth="md"
        fullWidth
      >
        <Formik
          enableReinitialize
          validationSchema={null}
          initialValues={preferencesInitialValues}
          onSubmit={() => {}}
          innerRef={formikRef}
        >
          {(props: FormikProps<PreferencesModel>) => {
            const { values, handleSubmit } = props;

            return values && !isEmpty(values) ? (
              <form
                className="relative flex flex-col md:flex-row m-2 space-y-4 md:space-y-0 overflow-y-auto"
                onSubmit={handleSubmit}
              >
                {loading && <LoadingData overlapParent />}
                <div className="flex flex-col w-full md:w-1/2 md:mr-5">
                  <FormGenerator
                    fieldsConfiguration={[
                      {
                        label: t`Map`,
                        hideColon: true,
                        property: '',
                        type: FormFieldTypeFG.CustomField,
                        containerClasses: 'mb-3',
                        renderer: () => {
                          return null;
                        },
                      } as CustomFormFieldFG,
                      {
                        label: t`Level/Spread Values`,
                        property: 'showLNS',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`CSQ`,
                        property: 'showCSQ',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`Site ID`,
                        property: 'showName',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: '',
                        innerSelectLabel: t`Show on map`,
                        property: 'showOption',
                        type: FormFieldTypeFG.Select,
                        returnKeyOnly: true,
                        availableValues: showOptionsFn(),
                        containerClasses: 'mb-2',
                      } as SelectFormFieldFG,
                      {
                        label: t`Show sites without location`,
                        property: 'showSitesWithoutLocation',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`GIS`,
                        property: 'showGIS',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`GIS DMA Boundary`,
                        property: 'showGISDMAData',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2 ml-4 sm:ml-8',
                      },
                    ]}
                    {...props}
                    formDisabled={false}
                    newEditableLook
                  />
                  {DmaLvlWrapper(
                    <FormGenerator
                      fieldsConfiguration={[
                        {
                          label: t`GIS Pipe Data`,
                          property: 'showGISPipeData',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2',
                        },
                        {
                          label: t`GIS Pipe Information`,
                          property: 'showGISPipeInformation',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2',
                        },
                        {
                          label: '',
                          property: 'gisPipeMetricSystem',
                          options: [
                            { label: t`Metric`, value: EMetricSystem.Metric },
                            {
                              label: t`Imperial`,
                              value: EMetricSystem.Imperial,
                            },
                          ],
                          type: FormFieldTypeFG.Radio,
                          inline: true,
                          containerClasses: 'mb-2 ml-4 sm:ml-8',
                        } as RadioFormFieldFG,
                        {
                          label: t`GIS Assets Data`,
                          property: 'showGISAssetsData',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2',
                        },
                        {
                          label: t`GIS Assets Ids`,
                          property: 'showGISAssetIds',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2',
                        },
                      ]}
                      {...props}
                      formDisabled={false}
                      newEditableLook
                    />,
                    true
                  )}
                  {DmaLvlWrapper(
                    <FormGenerator
                      fieldsConfiguration={[
                        {
                          label: t`Correlations`,
                          property: 'showCorrelations',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2',
                        },
                        {
                          label: t`High Confidence Only`,
                          property: 'showHighConfidenceOnly',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2 ml-4 sm:ml-8',
                        },
                        {
                          label: t`GIS Correlations Only`,
                          property: 'showGISCorrelations',
                          type: FormFieldTypeFG.Checkbox,
                          containerClasses: 'mb-2 ml-4 sm:ml-8',
                        },
                      ]}
                      {...props}
                      formDisabled={false}
                      newEditableLook
                    />
                  )}
                  <FormGenerator
                    fieldsConfiguration={[
                      {
                        label: t`Survey Measurements`,
                        property: 'showSurveyMeasurements',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`PRV Flogger`,
                        property: 'showPRVLogger',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`Flow Logger`,
                        property: 'showFlowLogger',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`Pressure Logger`,
                        property: 'showPressureLogger',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                      {
                        label: t`Flow and Pressure Logger`,
                        property: 'showFlowPressureLogger',
                        type: FormFieldTypeFG.Checkbox,
                        containerClasses: 'mb-2',
                      },
                    ]}
                    {...props}
                    formDisabled={false}
                    newEditableLook
                  />
                </div>
                <div className="flex flex-col w-full md:w-1/2">
                  <FormGenerator
                    fieldsConfiguration={[
                      {
                        label: t`Update Site Note`,
                        hideColon: true,
                        property: '',
                        type: FormFieldTypeFG.CustomField,
                        containerClasses: 'mb-3',
                        renderer: () => {
                          return null;
                        },
                      } as CustomFormFieldFG,
                      {
                        label: '',
                        innerSelectLabel: t`Date format`,
                        property: 'uploadSiteNoteDateFormat',
                        type: FormFieldTypeFG.Select,
                        returnKeyOnly: true,
                        availableValues: uploadSiteNoteDateFormatOptions(),
                        containerClasses: 'mb-2',
                      } as SelectFormFieldFG,
                    ]}
                    {...props}
                    formDisabled={false}
                    newEditableLook
                  />
                </div>
              </form>
            ) : null;
          }}
        </Formik>
      </CustomModal>
    </>
  );
};
